import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
  'data-testid'?: string;
  /**
   * Heading level for the title. Defaults to 'h3'.
   * Use 'h2' when EmptyState appears directly under a page-level <h1> to avoid
   * skipping a level in the heading hierarchy (FM4 / WCAG 2.4.6).
   */
  headingLevel?: 'h2' | 'h3';
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
  'data-testid': testId,
  headingLevel = 'h3',
}: EmptyStateProps) {
  const HeadingTag = headingLevel;
  return (
    <div
      className={cn('flex flex-col items-center justify-center py-12 px-4 text-center', className)}
      data-testid={testId}
    >
      {Icon && (
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Icon className="h-6 w-6 text-muted-foreground" />
        </div>
      )}
      <HeadingTag className="text-sm font-medium text-foreground">{title}</HeadingTag>
      {description && (
        <p className="mt-1 text-sm text-muted-foreground max-w-xs">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
